import { isFunction } from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useContext, useMemo } from 'react';
import { Button } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { isSizeInList } from '@bingads-webui-campaign-react/pmax-overview-responsive-utils';
import { withSiteMap, getRoutePathById } from '@bingads-webui-react/with-site-map';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';

import { ShimmerCard } from './shimmer-card';
import { NoAssetsCard, NO_ASSETS_STATE } from './no-assets-card';
import { AssetTable as AssetTableWithPerfLabel } from './asset-table';
import { AssetTableWithConversions } from './asset-table-with-conversions';
import { usePaginationConfig } from './use-pagination-config';
import { withFilteredAssetData } from './with-filtered-asset-data';
import { useCardStyles } from '../styles/card-styles';
import { AssetPerformanceCardProps, IScopeContext } from '../utils/types';

const ASSETS_PER_PAGE = 4;

export function AssetPerformanceCard({
  assets,
  isLoading,
  i18n,
  onActionButtonClick,
  perfMarkerUtils,
  responsiveSize,
  siteMap,
  isSDK,
  campaignType,
  permissions,
  logOnClick,
}: AssetPerformanceCardProps) {
  const styles = useCardStyles();

  const { scope } = useContext(ScopeContext) as IScopeContext;
  const instrumentationContextValue = useContext(InstrumentationContext) as any;

  const activityAndScenario = useMemo(() => (
    isFunction(instrumentationContextValue.createScenarioAndActivity) &&
    instrumentationContextValue.createScenarioAndActivity({
      scenarioName: 'Fluent2OverviewPage',
      activityName: 'AssetPerformanceCard',
    })), [instrumentationContextValue]);

  const AssetTable = useMemo(() => {
    const isConversionsEnabled = (permissions && permissions.dynamic && permissions.dynamic.AssetPerformanceTileWithConversions) || false;
    activityAndScenario?.activity?.trace(`Conversion metrics enabled: ${isConversionsEnabled}`);
    return isConversionsEnabled ? AssetTableWithConversions : AssetTableWithPerfLabel;
  }, [activityAndScenario, permissions]);

  const paginationConfig = usePaginationConfig(assets ?? [], ASSETS_PER_PAGE);
  const {
    current, total, onNext, onPrevious, resetToFirstPage,
  } = paginationConfig;

  const onNextClicked = useCallback(() => {
    logOnClick('onNextClicked pagination');
    onNext();
  }, [logOnClick, onNext]);
  const onPreviousClicked = useCallback(() => {
    logOnClick('onPreviousClicked pagination');
    onPrevious();
  }, [logOnClick, onPrevious]);

  const isSmallScreen = isSizeInList(responsiveSize, ['s', 'm']);
  const isCampaignTypeApp = (campaignType && campaignType === 'App') ?? false;

  const hasAssets = (assets ?? []).length > 0;
  const onClick = useCallback(() => {
    logOnClick('on navigate to asset groups');
    if (onActionButtonClick != null) {
      onActionButtonClick(hasAssets);
    } else if (isSDK) {
      if (scope?.values?.assetGroupId != null) {
        const route = getRoutePathById(siteMap, 'sdk-assets-settings');
        scope.save(route);
      } else {
        const {
          campaignId, aid, cid, uid,
        } = scope.values;
        const route = getRoutePathById(siteMap, 'asset-groups');
        const search = `cid=${cid}&uid=${uid}&aid=${aid}&campaignId=${campaignId}`;
        const url = `${route}?${search}`;
        window.open(url);
      }
    } else {
      const assetsRoute = isCampaignTypeApp ? getRoutePathById(siteMap, 'appads-asset-groups') : getRoutePathById(siteMap, 'asset-groups');
      const createAssetsRoute = isCampaignTypeApp ? getRoutePathById(siteMap, 'unified-appads-asset-group-create') : getRoutePathById(siteMap, 'asset-group-create');
      scope.save(assets ? assetsRoute : createAssetsRoute);
    }
  }, [hasAssets, assets, onActionButtonClick, scope, siteMap, isSDK, isCampaignTypeApp, logOnClick]);

  if (isLoading) {
    return (<ShimmerCard />);
  }

  perfMarkerUtils.done();

  return (
    <div className={classNames('ba-asset-performance-card', styles.container)}>
      {hasAssets ?
        <AssetTable logOnClick={logOnClick} i18n={i18n} assets={assets ?? []} paginationConfig={paginationConfig} assetsPerPage={ASSETS_PER_PAGE} resetToFirstPage={resetToFirstPage} isSmallScreen={isSmallScreen} />
        : <NoAssetsCard i18n={i18n} state={assets ? NO_ASSETS_STATE.NO_ASSETS_FILTERED : NO_ASSETS_STATE.NO_ASSETS} />
      }
      <div className={classNames('footer', styles.footer)}>
        <Button className="action-button" onClick={onClick}>{assets ? i18n.getString(_TL_('View asset groups')) : i18n.getString(_TL_('Add asset group'))}</Button>
        {hasAssets &&
          <div className="pagination-container">
            <Button onClick={onPreviousClicked as React.MouseEventHandler<HTMLButtonElement>} disabled={current <= 1} icon={<ChevronLeftRegular />} appearance="transparent" aria-label={i18n.getString('Previous')} />
            <span>{i18n.getString(_TL_('{{current}} of {{total}}'), { current, total })}</span>
            <Button onClick={onNextClicked as React.MouseEventHandler<HTMLButtonElement>} disabled={current >= total} icon={<ChevronRightRegular />} appearance="transparent" aria-label={i18n.getString('Next')} />
          </div>
        }
      </div >
    </div >
  );
}

export const AssetPerformanceCardWithAssetData = withSiteMap(withPermissions(withFilteredAssetData(AssetPerformanceCard)));
